import { getState, setState } from './store.js';
import { getParametre } from './api.js';
import { capitalizeFirstLetter } from './utils.js';

const UI_STAFF_LABEL_KEY = 'ui.staff_label';

function pluralizeFr(value) {
  if (typeof value !== 'string') return '';
  const v = value.trim();
  if (!v) return '';
  const last = v[v.length - 1];
  if (last === 's' || last === 'x' || last === 'z') return v;
  return v + 's';
}

function normalizeUiStaffLabel(raw) {
  if (!raw) return null;

  if (typeof raw === 'string') {
    const singular = raw.trim();
    if (!singular) return null;
    return { singular, plural: pluralizeFr(singular) };
  }

  if (typeof raw === 'object') {
    const singular = typeof raw.singular === 'string' ? raw.singular.trim() : '';
    const plural = typeof raw.plural === 'string' ? raw.plural.trim() : '';

    if (!singular && !plural) return null;
    const resolvedSingular = singular || (plural ? plural : '');
    const resolvedPlural = plural || (resolvedSingular ? pluralizeFr(resolvedSingular) : '');

    if (!resolvedSingular || !resolvedPlural) return null;
    return { singular: resolvedSingular, plural: resolvedPlural };
  }

  return null;
}

export async function ensureUiStaffLabelLoaded() {
  const { currentEtablissement, currentSite, uiStaffLabelSiteId } = getState();

  const siteId = currentSite?.id || null;
  const etablissementId = currentEtablissement?.id || null;

  if (!siteId || !etablissementId) {
    setState({ uiStaffLabel: null, uiStaffLabelSiteId: null });
    return { data: null, error: null };
  }

  if (uiStaffLabelSiteId && String(uiStaffLabelSiteId) === String(siteId)) {
    return { data: getState('uiStaffLabel') || null, error: null };
  }

  const { data, error } = await getParametre(etablissementId, siteId, UI_STAFF_LABEL_KEY);
  if (error) {
    setState({ uiStaffLabel: null, uiStaffLabelSiteId: siteId });
    return { data: null, error };
  }

  let parsed = null;
  if (data?.valeur) {
    if (data.type === 'json') {
      try {
        parsed = JSON.parse(data.valeur);
      } catch {
        parsed = data.valeur;
      }
    } else {
      parsed = data.valeur;
    }
  }

  const normalized = normalizeUiStaffLabel(parsed);
  setState({ uiStaffLabel: normalized, uiStaffLabelSiteId: siteId });
  return { data: normalized, error: null };
}

export function getUiStaffLabel({
  plural = false,
  fallbackSingular = 'Employé',
  fallbackPlural = 'Employés',
  capitalize = false,
} = {}) {
  const staffLabel = getState('uiStaffLabel');

  const resolved = staffLabel?.[plural ? 'plural' : 'singular']
    || (plural ? fallbackPlural : fallbackSingular);

  return capitalize ? capitalizeFirstLetter(resolved) : resolved;
}
